// apps/roleforge-app/src/components/chat/HostedCreditApprovalNotice.tsx
//
// Issue #1216 — this card explains why the conversation stopped, so it has to
// be readable. It used to be duplicated byte-for-byte in AuditConversePage and
// RespondShell, both hardcoding `bg-white` while the text used the theme
// tokens (`text-ink` / `text-graphite`). Under the default dark theme those
// tokens resolve to near-white, giving roughly 1.2:1 contrast on the white
// card, and the primary button was `bg-ink text-white` — light grey on white.
//
// There is now exactly one copy of the markup, and it paints on `bg-card`
// (the theme-aware elevated surface: #FFFFFF in light, #151B23 in dark) so
// the token text colours land the right way round in BOTH themes.
//
// Issue #1229 — the copy is plain business language: no "hosted execution",
// no "resume token", no "organization owner must approve this spend".

import { approvalEstimateCopy, type PendingRoleforgeApproval } from '../../lib/hosted-credit-approval';

interface Props {
  pending: PendingRoleforgeApproval;
  /** True while a retry is in flight — disables both actions. */
  busy?: boolean;
  onRetry: () => void;
  onDiscard: () => void;
  /** Positioning/layout classes supplied by the surface that renders it. */
  className?: string;
}

export function HostedCreditApprovalNotice({
  pending,
  busy = false,
  onRetry,
  onDiscard,
  className = '',
}: Props) {
  const estimate = approvalEstimateCopy(pending.details);

  return (
    <div
      data-testid="hosted-credit-approval"
      role="status"
      aria-live="polite"
      className={`rounded-xl border border-amber-400/70 bg-card p-4 shadow-soft ${className}`}
    >
      <p className="text-base font-semibold text-ink">Waiting for approval to continue</p>
      <p className="mt-1 text-sm text-graphite">
        Someone who handles billing for your account needs to approve this before
        we can carry on. Your answer is saved — once they have approved it,
        choose Try again and we will pick up exactly where we left off.
      </p>
      {estimate && <p className="mt-1 text-sm text-graphite">{estimate}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onRetry}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-paper transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral disabled:opacity-50"
        >
          {busy ? 'Trying again…' : 'Try again'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDiscard}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink transition hover:bg-card-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral disabled:opacity-50"
        >
          Discard
        </button>
      </div>
    </div>
  );
}
